import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ChevronDown, ChevronRight, Phone, Mail } from "lucide-react";
import { megaMenuData, navLinks } from "@/data/navigationData";

export function MobileMenu({ isOpen, onClose, currentPath }) {
  const [mobileExpanded, setMobileExpanded] = useState(null);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="lg:hidden overflow-hidden bg-[#1A120B] border-t border-[#C9922A]/10"
          data-testid="mobile-menu"
        >
          <div className="container-hh py-6 max-h-[80vh] overflow-y-auto">
            <nav className="flex flex-col gap-0.5">
              {navLinks.map((link, index) => {
                const hasMega = link.hasMega;
                const isActive = currentPath === link.path;
                const isExpanded = mobileExpanded === link.label;
                const data = hasMega ? megaMenuData[link.label] : null;

                return (
                  <motion.div
                    key={link.label}
                    initial={{ opacity: 0, x: -15 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.04 }}
                  >
                    {hasMega ? (
                      <>
                        <button
                          onClick={() => setMobileExpanded(isExpanded ? null : link.label)}
                          className={`flex items-center justify-between w-full py-3.5 px-5 rounded-xl text-sm uppercase tracking-[0.08em] transition-all duration-300 ${
                            isActive ? "text-[#C9922A] bg-[#C9922A]/[0.08]" : "text-white/50 hover:text-white hover:bg-white/[0.03]"
                          }`}
                        >
                          <span className="font-medium">{link.label}</span>
                          <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        <AnimatePresence>
                          {isExpanded && data && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              className="overflow-hidden"
                            >
                              <div className="mx-5 mt-2 mb-3 rounded-xl overflow-hidden h-28">
                                <img src={data.image} alt="" className="w-full h-full object-cover" />
                              </div>
                              <div className="pl-5 pr-3 pb-3 space-y-0.5">
                                {data.items.map((item, ci) => (
                                  <Link
                                    key={ci}
                                    to={item.path}
                                    onClick={onClose}
                                    className="flex items-start gap-3 py-3 px-3 rounded-lg hover:bg-[#C9922A]/[0.04] transition-all duration-200 group"
                                  >
                                    <span className="font-mono text-[10px] font-bold text-[#C9922A]/50 mt-0.5">{String(ci + 1).padStart(2, "0")}</span>
                                    <div>
                                      <span className="block text-sm text-white/60 group-hover:text-[#C9922A] transition-colors font-medium">{item.label}</span>
                                      <span className="block text-[11px] text-white/25 mt-0.5 leading-relaxed">{item.desc}</span>
                                    </div>
                                    <ChevronRight className="w-3 h-3 text-white/15 ml-auto mt-1 flex-shrink-0 group-hover:text-[#C9922A]/50 transition-colors" />
                                  </Link>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    ) : (
                      <Link
                        to={link.path}
                        onClick={onClose}
                        className={`flex items-center py-3.5 px-5 rounded-xl font-medium text-sm uppercase tracking-[0.08em] transition-all duration-300 ${
                          isActive ? "text-[#C9922A] bg-[#C9922A]/[0.08] border-l-[3px] border-[#C9922A]" : "text-white/50 hover:text-white hover:bg-white/[0.03]"
                        }`}
                      >
                        {link.label}
                      </Link>
                    )}
                  </motion.div>
                );
              })}
            </nav>

            <div className="mt-6">
              <Link
                to="/contact"
                onClick={onClose}
                className="flex items-center justify-center gap-2 py-3.5 px-6 bg-gradient-to-r from-[#C9922A] to-[#E8C874] text-[#1A120B] font-bold text-sm uppercase tracking-wider rounded-full"
                data-testid="mobile-cta-confier"
              >
                Confier mon bien <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-6 pt-6 border-t border-white/[0.06] flex flex-col gap-3">
              <a href="tel:+33767484313" className="flex items-center gap-3 text-sm text-white/40 hover:text-[#C9922A] transition-colors">
                <Phone className="w-4 h-4 text-[#C9922A]/60" /> +33 7 67 48 43 13
              </a>
              <a href="mailto:contact@happyhouse-conciergerie.fr" className="flex items-center gap-3 text-sm text-white/40 hover:text-[#C9922A] transition-colors">
                <Mail className="w-4 h-4 text-[#C9922A]/60" /> contact@happyhouse-conciergerie.fr
              </a>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
